import { isValidElement, type ReactNode } from 'react';
import { css, type Theme } from '@emotion/react';
import { mix } from 'polished';
import {
  type DropdownMenuContentProps as RadixDropdownMenuContentProps,
  Content as RadixDropdownMenuContent,
  Arrow as RadixDropdownMenuArrow,
} from '@radix-ui/react-dropdown-menu';
import { generateShadow } from 'client/Theme';
import { DropdownMenuItem, type DropdownMenuItemProps } from './DropdownMenuItem';

export interface DropdownMenuContentProps extends Omit<RadixDropdownMenuContentProps, 'onSelect'> {
  items: (DropdownMenuItemProps | JSX.Element)[];
  component?: JSX.Element;
  onSelect?: (id: string) => void;
}

export const DropdownMenuContent = ({ items, component = <RadixDropdownMenuContent />, onSelect, ...props }: DropdownMenuContentProps) => (
  <component.type sideOffset={5} {...props} css={[contentStyle, arrowStyle]}>
    {items.map((item) => {
      if (isValidElement(item)) return item as ReactNode;
      return <DropdownMenuItem key={(item as DropdownMenuItemProps).id} onSelect={onSelect} {...(item as DropdownMenuItemProps)} />;
    })}
    <RadixDropdownMenuArrow />
  </component.type>
);

export const popoverArrowURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg width='24' height='8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath ` +
  `fill-rule='evenodd' clip-rule='evenodd' d='M12.95.69A12 12 0 0 0 24 8H0A12 12 0 0 0 11.05.69a1 1 0 0 1 1.9 0Z' ` +
  `fill='${mix(0.5, theme.color.grey[60], theme.color.background).replace('#', '%23')}'/%3E%3C/svg%3E")`;

const contentStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background-color: ${mix(0.5, theme.color.grey[60], theme.color.background)};
  border-radius: ${theme.borderRadius * 2}px;
  box-shadow: ${generateShadow({ primary: theme.color.primary[100] })};
  z-index: 100;
`;

const arrowStyle = (theme: Theme) => css`
  > span {
    width: 30px;
    height: 8px;

    > svg {
      display: none !important;
    }

    ::before {
      // The cool triangle that points up
      content: '';
      display: block;
      position: absolute;
      bottom: -2px;
      width: 30px;
      height: 16px;
      background-image: ${popoverArrowURI(theme)};
      background-size: 100%;
      background-repeat: no-repeat;
      transform: scaleY(-1);
    }
  }
`;
